import React, { useCallback, useId, useState } from 'react';
import { Box, Popover } from '@mui/material';
import { DarkModeOutlined as DarkModeOutlinedIcon, WbSunny as WbSunnyIcon } from '@mui/icons-material';
import { useThemeId } from '../../theme/ThemeContext';
import { useTranslation } from 'react-i18next';

const iconSx = { fontSize: 20, flexShrink: 0 } as const;

type ThemeChoice = 'smsby' | 'dataedge';

const THEME_ORDER: ThemeChoice[] = ['smsby', 'dataedge'];

/** Тот же паттерн, что и LanguageSwitcher: кнопка 64×40 + Popover. */
const ThemeSelector: React.FC<{ inverted?: boolean }> = ({ inverted = false }) => {
  const { t } = useTranslation();
  const { themeId, setThemeId } = useThemeId();
  const listId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const menuBg = inverted ? '#000000' : '#eceff4';
  const optBorder = inverted ? '#fff' : 'rgba(13,13,13,0.35)';
  const optBorderMuted = inverted ? 'rgba(255,255,255,0.45)' : 'rgba(13,13,13,0.22)';

  const close = useCallback(() => setAnchorEl(null), []);

  const pick = useCallback(
    (id: ThemeChoice) => {
      setThemeId(id);
      close();
    },
    [setThemeId, close]
  );

  const currentLabel = themeId === 'smsby' ? t('theme.lightAria') : t('theme.darkAria');

  return (
    <>
      <Box
        component="button"
        type="button"
        id="theme-selector-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={currentLabel}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 64,
          height: 40,
          px: 1.25,
          py: 0.5,
          boxSizing: 'border-box',
          borderRadius: 1,
          cursor: 'pointer',
          m: 0,
          color: inverted ? '#fff' : 'inherit',
          bgcolor: inverted ? 'transparent' : 'rgba(255,255,255,0.92)',
          border: inverted ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(0,0,0,0.23)',
          '&:hover': {
            borderColor: inverted ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.4)',
          },
          '&:focus-visible': {
            outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
            outlineOffset: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {themeId === 'smsby' ? (
            <WbSunnyIcon sx={iconSx} />
          ) : (
            <DarkModeOutlinedIcon sx={iconSx} />
          )}
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        marginThreshold={8}
        slotProps={{
          paper: {
            elevation: 6,
            sx: {
              borderRadius: 1,
              overflow: 'hidden',
              border: inverted ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
              mt: 0.5,
              bgcolor: menuBg,
            },
          },
        }}
      >
        <Box
          id={listId}
          role="listbox"
          aria-labelledby="theme-selector-trigger"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1,
            minWidth: 52,
            boxSizing: 'border-box',
          }}
        >
          {THEME_ORDER.map((id) => {
            const selected = id === themeId;
            const label = id === 'smsby' ? t('theme.lightAria') : t('theme.darkAria');
            const Icon = id === 'smsby' ? WbSunnyIcon : DarkModeOutlinedIcon;
            return (
              <Box
                key={id}
                component="button"
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={label}
                onClick={() => pick(id)}
                sx={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  p: 0,
                  m: 0,
                  border: `2px solid ${selected ? optBorder : optBorderMuted}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: 'transparent',
                  color: inverted ? '#fff' : 'inherit',
                  opacity: selected ? 1 : inverted ? 0.55 : 0.65,
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.06)',
                  },
                  '&:focus-visible': {
                    outline: inverted ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(25,118,210,0.6)',
                    outlineOffset: 2,
                  },
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default ThemeSelector;
